/**
 * What closing a position costs.
 *
 * The obvious metric is wrong, and the module exists because every airport
 * dashboard uses it. Counting the flights a position serves misleads in both
 * directions: a stand carrying twenty flights a day among ten identical
 * neighbours costs almost nothing to close at 02:00, because the traffic goes
 * next door; a stand carrying three flights that is one of only two taking a
 * code-E widebody is a different proposition entirely.
 *
 * The cost of closing a position is the traffic that has NOWHERE ELSE TO GO.
 * So the answer is not a count but a disposition, and only the last number in
 * it is worth an argument:
 *
 *     14 movements: 12 absorbed, 1 bussed from a remote stand, 1 stranded.
 *
 * A COUNTERFACTUAL, AND LABELLED AS ONE. "These flights would have used this
 * position" describes a world that did not happen. Printed beside a real
 * figure it will be read as one, so `assumption` travels with every answer and
 * a screen must show it.
 */
import { t, tn } from "../i18n";

/** How one movement fares if the position closes. */
export type Disposition = "absorbed" | "bussed" | "stranded" | "undetermined";

export interface MovementDetail {
  flight?: string;
  sizeCode?: string;
  unitClass?: string;
  occupiedFrom?: string;
  movesTo?: string;
  why?: string;
}

export interface ClosureCost {
  position: string;
  window: { from: string; to: string };
  /** False when the schedule does not cover the window — not a zero cost. */
  known: boolean;
  whyNot?: string;
  movements: number;
  absorbed: number;
  bussed: number;
  /** The only number worth an argument. */
  stranded: number;
  /** Movements whose fate could not be decided — neither absorbed nor stranded. */
  undetermined: number;
  hours?: number;
  scheduleCoverage?: string;
  /** The counterfactual, stated. */
  assumption?: string;
  detail: MovementDetail[];
}

export interface CriticalPosition {
  position: string;
  stranded: number;
  bussed?: number;
  movements?: number;
  undetermined?: number;
}

export interface CriticalReport {
  window: { from: string; to: string };
  known: boolean;
  whyNot?: string;
  /** Positions that cannot be closed without stranding traffic. */
  critical: CriticalPosition[];
  /** Positions whose traffic the rest of the estate can absorb. */
  flexible: CriticalPosition[];
  note?: string;
  scheduleCoverage?: string;
}

/**
 * Whether closing this position is free, awkward, or not possible.
 *
 * Bussing is deliberately its own band rather than folded into "fine". A
 * bussed passenger is a real operational cost and a real passenger complaint;
 * it is simply not the same cost as an aircraft with nowhere to park.
 */
export type ClosureVerdict = "unknown" | "free" | "bussing" | "stranding";

export function verdict(c: ClosureCost): ClosureVerdict {
  if (!c.known) return "unknown";
  if (c.stranded > 0) return "stranding";
  if (c.bussed > 0) return "bussing";
  return "free";
}

export const VERDICT_LABEL: Record<ClosureVerdict, string> = {
  unknown: "Cannot be assessed",
  free: "Absorbed by the rest of the estate",
  bussing: "Absorbed, but some passengers are bussed",
  stranding: "Some aircraft have nowhere to go",
};

/**
 * The sentence a planner reads first.
 *
 * Undetermined movements are named rather than folded into either side.
 * Counting them as absorbed understates the cost and counting them as
 * stranded overstates it, and both are guesses about a movement the platform
 * declined to decide.
 */
export function disposition(c: ClosureCost): string {
  if (!c.known) return c.whyNot ?? t("The schedule does not cover this window.");
  const parts = [t("{n} absorbed", { n: c.absorbed })];
  if (c.bussed) parts.push(t("{n} bussed", { n: c.bussed }));
  if (c.undetermined) parts.push(t("{n} undetermined", { n: c.undetermined }));
  parts.push(t("{n} with nowhere to go", { n: c.stranded }));
  return t("{movements} in the window: {parts}.", {
    movements: tn(c.movements, "{n} movement", "{n} movements"),
    parts: parts.join(", "),
  });
}
